/**
 * What a structured import reports beyond the guide itself: the backend's leftovers, its notes,
 * the image placements it refused, and what the generation cost.
 *
 * None of it is stored on the guide (the record has no field for it, and contentJson is public),
 * so it reaches the operator only through the import's `done` event. The BFF also writes the usage
 * to its log as `[guides] import usage ...`, which is the record the pilot totals from.
 */

export interface GuideUsage {
  model?: string | null;
  inputTokens?: number | null;
  outputTokens?: number | null;
  reasoningTokens?: number | null;
  estimatedCostUsd?: number | null;
}

export interface ImportReportData {
  /** Rule violations the backend could not fix on its retry. Worth fixing before publishing. */
  problems: string[];
  /** "Note: " lines: content gaps and the safety nets the backend applied. Informational. */
  notes: string[];
  /** Image placements the backend removed from the model's answer. */
  placementsRemoved: string[];
  usage: GuideUsage | null;
}

const NOTE = /^note:\s*/i;

/** Split the backend's one `problems` list into the two things it carries. */
export function splitProblems(problems: unknown): { problems: string[]; notes: string[] } {
  const all = Array.isArray(problems) ? problems.filter((p): p is string => typeof p === 'string' && !!p.trim()) : [];
  return {
    problems: all.filter((p) => !NOTE.test(p)),
    notes: all.filter((p) => NOTE.test(p)).map((p) => p.replace(NOTE, '')),
  };
}

/** The report from an import's `done` event, or null when there is nothing structured to say. */
export function reportFromDone(evt: Record<string, any> | null | undefined): ImportReportData | null {
  const guide = evt?.guide;
  if (!guide || guide.format !== 'structured') return null;
  const { problems, notes } = splitProblems(guide.problems);
  return {
    problems,
    notes,
    placementsRemoved: Array.isArray(evt?.placementsRemoved) ? evt!.placementsRemoved : [],
    usage: evt?.usage ?? null,
  };
}

/** A cost in dollars at the precision it deserves: a nano run costs fractions of a cent. */
export function formatCost(usd: number | null | undefined): string {
  if (typeof usd !== 'number' || !Number.isFinite(usd)) return 'cost unknown';
  if (usd === 0) return '$0';
  return `$${usd.toFixed(usd < 0.1 ? 4 : 2)}`;
}

const n = (v: number | null | undefined) => (typeof v === 'number' && Number.isFinite(v) ? v : 0).toLocaleString('en-US');

/** One line: model, tokens in and out, reasoning, and the estimate. */
export function costLine(usage: GuideUsage | null | undefined): string {
  if (!usage) return 'Usage was not reported for this generation.';
  const reasoning = usage.reasoningTokens ? ` (${n(usage.reasoningTokens)} reasoning)` : '';
  return (
    `${usage.model || 'Unknown model'}: ${n(usage.inputTokens)} tokens in, ` +
    `${n(usage.outputTokens)} out${reasoning}. Estimated ${formatCost(usage.estimatedCostUsd)}.`
  );
}
